import * as React from 'react';
import { Box } from '@chakra-ui/react';

const TURNSTILE_SCRIPT_ID = 'cloudflare-turnstile-script';
const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_LOAD_TIMEOUT_MS = 15000;
const VERIFICATION_TIMEOUT_MS = 2 * 60 * 1000;

type TurnstileOptions = {
  sitekey: string;
  action: string;
  appearance: 'interaction-only';
  execution: 'execute';
  language: string;
  retry: 'never';
  size: 'compact' | 'flexible';
  theme: 'auto';
  'response-field': false;
  callback: (token: string) => void;
  'error-callback': (errorCode: string) => boolean;
  'expired-callback': () => void;
  'timeout-callback': () => void;
  'unsupported-callback': () => void;
};

type TurnstileApi = {
  render: (container: HTMLElement, options: TurnstileOptions) => string;
  execute: (widgetId: string) => void;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

type PendingVerification = {
  resolve: (token: string) => void;
  reject: (error: Error) => void;
  timeout: number;
};

export type NewsletterTurnstileHandle = {
  verify: () => Promise<string>;
  reset: () => void;
};

type Props = {
  language: string;
  siteKey: string;
};

let turnstileScriptRequest: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (typeof window === 'undefined') return Promise.reject(new Error('TURNSTILE_UNAVAILABLE'));
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (turnstileScriptRequest) return turnstileScriptRequest;

  turnstileScriptRequest = new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script');
    const timeout = window.setTimeout(() => reject(new Error('TURNSTILE_UNAVAILABLE')), SCRIPT_LOAD_TIMEOUT_MS);
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.clearTimeout(timeout);
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('TURNSTILE_UNAVAILABLE'));
    };
    script.onerror = () => {
      window.clearTimeout(timeout);
      reject(new Error('TURNSTILE_UNAVAILABLE'));
    };
    document.head.appendChild(script);
  }).catch(error => {
    document.getElementById(TURNSTILE_SCRIPT_ID)?.remove();
    turnstileScriptRequest = null;
    throw error;
  });

  return turnstileScriptRequest;
}

export const NewsletterTurnstile = React.forwardRef<NewsletterTurnstileHandle, Props>(
  ({ language, siteKey }, forwardedRef) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const widgetIdRef = React.useRef<string | null>(null);
    const pendingRef = React.useRef<PendingVerification | null>(null);
    const isMountedRef = React.useRef(true);

    const settle = React.useCallback((result: { token: string } | { error: Error }) => {
      const pending = pendingRef.current;
      if (!pending) return;

      window.clearTimeout(pending.timeout);
      pendingRef.current = null;
      if ('token' in result) pending.resolve(result.token);
      else pending.reject(result.error);
    }, []);

    const ensureWidget = React.useCallback(async () => {
      if (widgetIdRef.current) return widgetIdRef.current;

      const turnstile = await loadTurnstile();
      if (!isMountedRef.current || !containerRef.current) throw new Error('TURNSTILE_UNAVAILABLE');
      const size = containerRef.current.clientWidth < 300 ? 'compact' : 'flexible';

      const widgetId = turnstile.render(containerRef.current, {
        sitekey: siteKey,
        action: 'newsletter_signup',
        appearance: 'interaction-only',
        execution: 'execute',
        language,
        retry: 'never',
        size,
        theme: 'auto',
        'response-field': false,
        callback: token => settle({ token }),
        'error-callback': () => {
          settle({ error: new Error('TURNSTILE_FAILED') });
          return true;
        },
        'expired-callback': () => settle({ error: new Error('TURNSTILE_EXPIRED') }),
        'timeout-callback': () => settle({ error: new Error('TURNSTILE_TIMEOUT') }),
        'unsupported-callback': () => settle({ error: new Error('TURNSTILE_UNSUPPORTED') })
      });
      widgetIdRef.current = widgetId;
      return widgetId;
    }, [language, settle, siteKey]);

    const reset = React.useCallback(() => {
      settle({ error: new Error('TURNSTILE_RESET') });
      const widgetId = widgetIdRef.current;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
      widgetIdRef.current = null;
    }, [settle]);

    React.useImperativeHandle(
      forwardedRef,
      () => ({
        verify: () => {
          if (pendingRef.current) throw new Error('TURNSTILE_PENDING');

          return new Promise<string>((resolve, reject) => {
            const pending: PendingVerification = {
              resolve,
              reject,
              timeout: window.setTimeout(
                () => settle({ error: new Error('TURNSTILE_TIMEOUT') }),
                VERIFICATION_TIMEOUT_MS
              )
            };
            pendingRef.current = pending;

            ensureWidget()
              .then(widgetId => {
                if (pendingRef.current !== pending) return;
                if (!window.turnstile) throw new Error('TURNSTILE_UNAVAILABLE');
                window.turnstile.execute(widgetId);
              })
              .catch(() => {
                if (pendingRef.current === pending) settle({ error: new Error('TURNSTILE_FAILED') });
              });
          });
        },
        reset
      }),
      [ensureWidget, reset, settle]
    );

    React.useEffect(() => {
      isMountedRef.current = true;

      return () => {
        isMountedRef.current = false;
        settle({ error: new Error('TURNSTILE_UNAVAILABLE') });
        const widgetId = widgetIdRef.current;
        if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
        widgetIdRef.current = null;
      };
    }, [language, settle, siteKey]);

    return <Box ref={containerRef} w="full" maxW="100%" minW={0} />;
  }
);

NewsletterTurnstile.displayName = 'NewsletterTurnstile';
